import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { AuthContext } from '../../context/auth/auth-context'
import { Instant, ZoneId } from '@js-joda/core'
import useSWR from 'swr'
import { basicFetcher } from '../../utils/api/swr-fetcher'
import { Box, Card, CardContent, Chip, FormControl, Grid, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material'
import { useExposureSnapshots } from '../../hooks/data/use-exposure-snapshots'
import { formatToLocalPatternFromUTCNumber } from '../../utils/time-utils'
import { type PortfolioExposureData } from '../../models/exposure/exposure-types'
import { MODE_OPTIONS } from '../../components/data-grid/wrapped-data-grid/wrapped-data-grid-types'
import StackColumnLayout from '../../layouts/stack-column-layout'
import ExposureDataGrid from './exposure-data-grid'
import ExposureDataGridStatic from './exposure-data-grid-static'

const TIME_RANGE_HOURS = 12

const MultiExposureDataGrid: React.FC = () => {
  const [portfolios, setPortfolios] = useState<string[]>([])
  const { data, error } = useSWR<PortfolioExposureData, boolean>(
    process.env.REACT_APP_API_EXPOSURE,
    basicFetcher,
    {
      shouldRetryOnError: true,
      refreshInterval: 5000
    }
  )

  const [isSnapshotMode, setIsSnapshotMode] = useState<boolean>(false)
  const [after, setAfter] = useState<string>('')

  const { isAuthorized, accessToken } = useContext(AuthContext)
  const snapshots = useExposureSnapshots(isAuthorized, accessToken, after)

  useEffect(() => {
    if (error === true || data === undefined) {
      setPortfolios([])
    } else {
      setPortfolios(Object.keys(data).filter(key => key !== 'timestamp'))
    }
  }, [data, error])

  const [selectedTimeRange, setSelectedTimeRange] = React.useState<string>('1')
  const [selectedTimestamp, setSelectedTimestamp] = React.useState<string>('')

  useEffect(() => {
    const now = Instant.ofEpochMilli(Date.now()).atZone(ZoneId.systemDefault())
    const lookupTime = now.minusHours(Number(selectedTimeRange)).toEpochSecond() * 1000
    setAfter(String(lookupTime))
  }, [selectedTimeRange])

  const timeRangeMenuItems = useMemo<React.ReactNode[]>(() => {
    const nodes = []
    for (let i = 1; i <= TIME_RANGE_HOURS; i++) {
      nodes.push(
        <MenuItem key={i} value={i}>
          {`~${i} hour${i > 1 ? 's' : ''} ago`}
        </MenuItem>
      )
    }
    return nodes
  }, [])

  const snapshotMenuItems = useMemo<React.ReactNode[]>(() => {
    return Object.keys(snapshots)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .reverse()
      .map(timestamp => {
        return <MenuItem key={timestamp} value={timestamp}>
          {formatToLocalPatternFromUTCNumber(timestamp, 'hh:mm a')}
        </MenuItem>
      })
  }, [snapshots])

  const prevSelectedTimestamp = useRef<string>('')
  const [selectedSnapshot, setSelectedSnapshot] = useState<PortfolioExposureData | null>(null)
  useEffect(() => {
    if (prevSelectedTimestamp.current !== selectedTimestamp) {
      prevSelectedTimestamp.current = selectedTimestamp
      setSelectedSnapshot(snapshots[Number(selectedTimestamp)])
    }
  }, [selectedTimestamp, snapshots])

  const staticDataGrids = useMemo(() => {
    return (
      <Box>
        {
          portfolios.map(portfolio => <ExposureDataGridStatic
            key={`${portfolio}-exposure-data-grid-static`}
            portfolio={portfolio}
            dataMode={MODE_OPTIONS.COIN}
            data={selectedSnapshot}
          />)
        }
      </Box>
    )
  }, [selectedSnapshot])

  return (
    <StackColumnLayout>
      <Box padding={1}>
        <Stack direction='row' gap={1}>
          <Typography color='secondary'>Mode:</Typography>
          <Chip
            label='Current'
            onClick={() => {
              setIsSnapshotMode(false)
            }}
            variant='outlined'
            size='small'
            color={!isSnapshotMode ? 'primary' : undefined}
          />
          <Chip
            label='Historical'
            onClick={() => {
              setIsSnapshotMode(true)
            }}
            variant='outlined'
            size='small'
            color={isSnapshotMode ? 'primary' : undefined}
          />
        </Stack>
        {isSnapshotMode &&
          <Stack gap={0} marginTop={1}>
            <Typography variant='subtitle2' gutterBottom>
              Select a snapshot to display:
            </Typography>
            <Card variant='outlined'>
              <CardContent>
                <Grid container padding={2} spacing={2} alignItems='flex-end'>
                  <Grid item xs={12} md={2}>
                    <FormControl variant='standard' sx={{ width: '140px', marginRight: 2 }}>
                      <InputLabel>Time Range</InputLabel>
                      <Select
                        label='Time Range'
                        value={selectedTimeRange}
                        onChange={event => {
                          setSelectedTimeRange(event.target.value)
                        }}
                      >
                        {timeRangeMenuItems}
                      </Select>
                    </FormControl>

                    <FormControl variant='standard' sx={{ width: '130px' }}>
                      <InputLabel>Snapshot</InputLabel>
                      <Select
                        label='Snapshot'
                        value={selectedTimestamp}
                        defaultValue={''}
                        onChange={event => {
                          setSelectedTimestamp(event.target.value)
                        }}
                      >
                        {snapshotMenuItems}

                        {/* Suppress the out-of-range value warning */}
                        {selectedTimestamp !== '' && (
                          <MenuItem key={selectedTimestamp} value={selectedTimestamp} sx={{ display: 'none' }}>
                            {formatToLocalPatternFromUTCNumber(selectedTimestamp, 'hh:mm a')}
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={10}>
                    <Typography variant='subtitle2' gutterBottom>
                      Note: Each snapshot is saved at approximately every 5 minutes, looking back over the past {TIME_RANGE_HOURS} hours
                      only.
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Stack>
        }
      </Box>

      {isSnapshotMode && staticDataGrids}

      {!isSnapshotMode &&
        <Box>
          {
            portfolios.map(portfolio => <ExposureDataGrid key={`${portfolio}-exposure-data-grid`} portfolio={portfolio}/>)
          }
        </Box>
      }
    </StackColumnLayout>
  )
}

export default MultiExposureDataGrid
